import { ReactElement, useEffect, useState, useContext } from "react";
import "./AboutUs.css";
import GeneralLayoutWithDots from "./components/GeneralLayoutWithDots";
import { IBod } from "../../Services/Bod";
import { useNavigate } from "react-router-dom";
import { axios } from "../..";
import AuthContext from "../../store/AuthContext";
import { getCountryName } from "../../utils/formatNames";
import { specialCountries } from "../../utils/const";
import { ReactComponent as EmailIcon } from "../../assets/images&icons/email.svg";
import ProfilePhoto from "../../components/Atomos/ProfilePhoto";
import { resolveProfilePictureDisplayUrl } from "../../utils/profilePictureUrl";
import {
  buildFirmProfilePath,
  buildPersonProfilePath,
} from "../../utils/publicProfileUrls";

const OFFICER_TITLE_ORDER = [
  "chairman",
  "chairman elect",
  "secretary",
  "treasurer",
  "chairman emeritus",
] as const;

function normalizeOfficerTitle(title?: string): string {
  return String(title ?? "")
    .toLowerCase()
    .replace(/[-–—]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function officerTitleRank(title?: string): number {
  const normalized = normalizeOfficerTitle(title);
  if (!normalized) {
    return OFFICER_TITLE_ORDER.length;
  }

  const exact = OFFICER_TITLE_ORDER.indexOf(
    normalized as (typeof OFFICER_TITLE_ORDER)[number],
  );
  if (exact >= 0) {
    return exact;
  }

  if (normalized.includes("emeritus")) {
    return 4;
  }
  if (normalized.includes("elect")) {
    return 1;
  }
  if (normalized.includes("secretary")) {
    return 2;
  }
  if (normalized.includes("treasurer")) {
    return 3;
  }
  if (normalized === "chair" || normalized.includes("chairman")) {
    return 0;
  }

  return OFFICER_TITLE_ORDER.length;
}

function compareOfficerTitles(left?: string, right?: string): number {
  const rankDelta = officerTitleRank(left) - officerTitleRank(right);
  if (rankDelta !== 0) {
    return rankDelta;
  }
  return normalizeOfficerTitle(left).localeCompare(
    normalizeOfficerTitle(right),
  );
}

function bodPersonPhotoSrc(person?: { id?: string; photo?: string }): string {
  if (!person?.id) {
    return "";
  }
  return resolveProfilePictureDisplayUrl(person.id, person.photo);
}

// Enhanced BOD type with joined Person and Firm data
interface BodWithJoinedData extends IBod {
  person?: {
    id?: string;
    photo: string;
    name: string;
    lastName: string;
    firmEmail: string;
    listTitle: string;
    firmName: string;
    mainOfficeName: string;
  };
  firm?: {
    legacyId: string;
    country: string;
    state: string;
  };
}

function compareBodByLastName(
  left: BodWithJoinedData,
  right: BodWithJoinedData,
): number {
  const lastNameCompare = (left.person?.lastName ?? "").localeCompare(
    right.person?.lastName ?? "",
  );
  if (lastNameCompare !== 0) {
    return lastNameCompare;
  }
  return (left.person?.name ?? "").localeCompare(right.person?.name ?? "");
}

type BodPerson = BodWithJoinedData["person"];

function BodPersonLink({
  person,
  contactId,
  className,
}: {
  person?: BodPerson;
  contactId?: string;
  className: string;
}): ReactElement {
  const path = buildPersonProfilePath(
    person?.name,
    person?.lastName,
    contactId,
  );
  const label = person ? `${person.name} ${person.lastName}` : "N/A";

  if (!path) {
    return <span className={className}>{label}</span>;
  }

  return (
    <a
      href={path}
      className={`${className} hover:text-red-600 hover:underline transition-colors`}
    >
      {label}
    </a>
  );
}

function BodFirmLink({
  firmName,
  legacyId,
  label,
  className,
}: {
  firmName?: string;
  legacyId?: string;
  label?: string;
  className: string;
}): ReactElement {
  const path = buildFirmProfilePath(firmName, legacyId);
  const text = label ?? firmName ?? "World Services Group";

  if (!path) {
    return <span className={className}>{text}</span>;
  }

  return (
    <a
      href={path}
      className={`${className} hover:text-red-600 hover:underline transition-colors`}
    >
      {text}
    </a>
  );
}

function BodEmailButton({
  email,
  personName,
  isCorporateLeadership,
}: {
  email: string;
  personName: string;
  isCorporateLeadership?: boolean;
}): ReactElement {
  const buttonClassName = isCorporateLeadership
    ? "w-6 h-6 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors flex items-center justify-center"
    : "w-6 h-6 bg-blue-600 text-white rounded-full hover:bg-red-600 transition-colors flex items-center justify-center";

  return (
    <button
      onClick={() => window.open(`mailto:${email}`, "_blank")}
      className={buttonClassName}
      title={`Email ${personName}`}
    >
      <EmailIcon />
    </button>
  );
}

export default function Board(): ReactElement {
  const navigate = useNavigate();
  const [allBods, setAllBods] = useState<BodWithJoinedData[]>([]);
  const [loading, setLoading] = useState(true);
  const { loggedPerson } = useContext(AuthContext);

  useEffect(() => {
    const fetchBodData = async () => {
      try {
        setLoading(true);
        // Fetch all BOD data with joined Person and Firm data
        const response = await axios.Get("/bod");
        setAllBods(response.data);
      } catch (error) {
        console.error("Error fetching BOD data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBodData();
  }, []);

  const handleBylawsNavigation = () => {
    if (loggedPerson) {
      navigate("/bylaws", { state: { fromBoard: true } });
    } else {
      navigate("/login");
      // Scroll to top when navigating to login
      setTimeout(() => {
        window.scrollTo(0, 0);
        const contentDiv = document.getElementById("content-container");
        if (contentDiv) {
          contentDiv.scrollTop = 0;
        }
      }, 100);
    }
  };
  return (
    <div className="w-full">
      <GeneralLayoutWithDots subTitle="More About WSG" CurrentPage="Board">
        <>
          {/* Board of Directors Content Section */}
          <div className="about-us-page-content py-4 xs:py-5 sm:py-6 md:py-8">
            {/* Main Title */}
            <h1 className="board-title text-left">Board of Directors</h1>

            {/* Main Content - Full Width */}
            <div className="w-full">
              {/* Board of Directors Introduction */}
              <div className="mb-8 xs:mb-9 sm:mb-10 md:mb-12 lg:mb-14">
                <p className="board-paragraph text-left">
                  World Services Group is governed by a Board of Directors
                  representative of the membership as a whole. Briefly stated,
                  the Board's role is to ensure that WSG establishes and
                  maintains the trust of its members, is prudent and ethical in
                  its activities, and is accountable for its actions. The Board
                  meetings focus on planning, policy-making and assessing the
                  progress of our mission. WSG is governed by{" "}
                  <button
                    onClick={handleBylawsNavigation}
                    className="text-blue-600 hover:text-red-600 hover:underline transition-colors cursor-pointer bg-transparent border-none p-0 font-inherit"
                  >
                    Bylaws
                  </button>{" "}
                  which provide the framework needed for WSG operations and
                  governance.
                </p>
              </div>

              {/* Separator Line */}
              <hr className="border-gray-100 mb-8" />

              {/* Officers Section */}
              <div className="mb-8 xs:mb-9 sm:mb-10 md:mb-12 lg:mb-14 mt-8 xs:mt-9 sm:mt-10 md:mt-12 lg:mt-14">
                <h2 className="council-region-title mb-8 text-left">
                  Officers
                </h2>

                {loading ? (
                  <div className="text-center py-4">
                    <p className="text-gray-600">Loading Officers...</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 xs:gap-5 sm:gap-6 md:gap-8">
                    {allBods
                      .filter((bod) => bod.IsOfficer && bod.isActive)
                      .sort((left, right) =>
                        compareOfficerTitles(left.Title, right.Title),
                      )
                      .map((officer, index) => (
                        <div
                          key={officer.id || index}
                          className="flex items-start"
                        >
                          <div className="w-16 h-16 xs:w-20 xs:h-20 sm:w-24 sm:h-24 md:w-[90px] md:h-[90px] rounded-full mr-3 xs:mr-4 sm:mr-5 md:mr-6 flex-shrink-0 overflow-hidden flex items-center justify-center">
                            <ProfilePhoto
                              src={bodPersonPhotoSrc(officer.person)}
                              alt={`${officer.person?.name ?? ""} ${officer.person?.lastName ?? ""}`}
                              imgClassName="w-full h-full object-cover object-[center_25%]"
                            />
                          </div>
                          <div>
                            <h3 className="council-chair mb-1 text-left">
                              {officer.Title || "Officer"}
                            </h3>
                            <div className="flex items-center gap-2 mb-0">
                              <BodPersonLink
                                person={officer.person}
                                contactId={officer.ContactId}
                                className="council-name text-left"
                              />
                              {officer.person?.firmEmail && (
                                <>
                                  <div className="w-0.5 h-4 bg-gray-300"></div>
                                  <BodEmailButton
                                    email={officer.person.firmEmail}
                                    personName={`${officer.person.name} ${officer.person.lastName}`}
                                    isCorporateLeadership={officer.IsWSG}
                                  />
                                </>
                              )}
                            </div>
                            <p className="council-title mb-2 text-left italic">
                              {officer.person?.listTitle || "Partner"}
                            </p>
                            <div className="space-y-0">
                              <BodFirmLink
                                firmName={officer.person?.firmName}
                                legacyId={officer.firm?.legacyId}
                                className="council-firm text-left block"
                              />
                              <p className="council-services text-left">
                                Legal Services
                              </p>
                              {officer.firm?.country && (
                                <p className="council-address text-left italic text-[#474F56]">
                                  {specialCountries.includes(
                                    officer.firm.country,
                                  )
                                    ? officer.firm.state +
                                      ", " +
                                      getCountryName(officer.firm.country)
                                    : getCountryName(officer.firm.country)}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    {allBods.filter((bod) => bod.IsOfficer && bod.isActive)
                      .length === 0 && (
                      <div className="col-span-full text-center py-4">
                        <p className="text-gray-600">
                          No active officers found.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Separator Line */}
              <hr className="border-gray-100 mb-8" />

              {/* Directors Section */}
              <div className="mb-8 xs:mb-9 sm:mb-10 md:mb-12 lg:mb-14 mt-8 xs:mt-9 sm:mt-10 md:mt-12 lg:mt-14">
                <h2 className="council-region-title mb-8 text-left">
                  Directors
                </h2>

                {loading ? (
                  <div className="text-center py-4">
                    <p className="text-gray-600">Loading Directors...</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 xs:gap-5 sm:gap-6">
                    {allBods
                      .filter((bod) => bod.IsDirector && bod.isActive)
                      .sort(compareBodByLastName)
                      .map((director, index) => (
                        <div
                          key={director.id || index}
                          className="flex items-start"
                        >
                          <div className="w-16 h-16 xs:w-20 xs:h-20 sm:w-24 sm:h-24 md:w-[90px] md:h-[90px] rounded-full mr-3 xs:mr-4 sm:mr-5 md:mr-6 flex-shrink-0 overflow-hidden flex items-center justify-center">
                            <ProfilePhoto
                              src={bodPersonPhotoSrc(director.person)}
                              alt={`${director.person?.name ?? ""} ${director.person?.lastName ?? ""}`}
                              imgClassName="w-full h-full object-cover object-[center_25%]"
                            />
                          </div>
                          <div>
                            {director.Title && director.Title.trim() !== "" && (
                              <p className="council-chair mb-1 text-left">
                                {director.Title}
                              </p>
                            )}
                            <div className="flex items-center gap-2 mb-0">
                              <BodPersonLink
                                person={director.person}
                                contactId={director.ContactId}
                                className="council-name text-left"
                              />
                              {director.person?.firmEmail && (
                                <>
                                  <div className="w-0.5 h-4 bg-gray-300"></div>
                                  <BodEmailButton
                                    email={director.person.firmEmail}
                                    personName={`${director.person.name} ${director.person.lastName}`}
                                    isCorporateLeadership={director.IsWSG}
                                  />
                                </>
                              )}
                            </div>
                            <p className="council-title mb-2 text-left italic">
                              {director.person?.listTitle || "Partner"}
                            </p>
                            <div className="space-y-0">
                              <BodFirmLink
                                firmName={director.person?.firmName}
                                legacyId={director.firm?.legacyId}
                                className="council-firm text-left block"
                              />
                              <p className="council-services text-left">
                                Legal Services
                              </p>
                              {director.firm?.country && (
                                <p className="council-address text-left italic text-[#474F56]">
                                  {specialCountries.includes(
                                    director.firm.country,
                                  )
                                    ? director.firm.state +
                                      ", " +
                                      getCountryName(director.firm.country)
                                    : getCountryName(director.firm.country)}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    {allBods.filter((bod) => bod.IsDirector && bod.isActive)
                      .length === 0 && (
                      <div className="col-span-full text-center py-4">
                        <p className="text-gray-600">
                          No active directors found.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* Separator Line */}
              <hr className="border-gray-100 mb-8" />
            </div>
          </div>
        </>
      </GeneralLayoutWithDots>
    </div>
  );
}
